// StoreSellerDetailPage.js
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Table, Card, Button, Modal, Badge } from "react-bootstrap";
import axios from "axios";
import { TranslatorContext } from "../../../../context/Translator";
import PageLayout from "../../../../layouts/PageLayout";
import LabelFieldComponent from "../../../../components/fields/LabelFieldComponent";
import AddStoreSellerPaymentModal from "./AddStoreSellerPaymentModal";
import PaymentApprovalButton from "../PaymentApprovalButton";

export default function StoreSellerDetailPage() {
    const { id: sellerId } = useParams();
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);
    const [activeTab, setActiveTab] = useState("products");
    const [detail, setDetail] = useState(null);
    const [search, setSearch] = useState("");
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";
    const resolveImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${imgBaseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };
    const money = (amount) => Number(amount || 0).toFixed(2);

    const fetchDetails = useCallback(async (searchQuery = "") => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/store-sellers/${sellerId}`,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
                    params: searchQuery ? { search: searchQuery } : {},
                }
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load store seller details.");
            }
            setDetail(response.data.data || {});
        } catch (err) {
            console.error("Error fetching store seller details:", err);
            setError(err.response?.data?.message || err.message || "Failed to fetch store seller details.");
        } finally {
            setLoading(false);
        }
    }, [sellerId]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    const seller = detail?.store_seller || {};
    const profile = detail?.store_seller?.store_seller || {};
    const summary = detail?.summary || {};
    const products = detail?.products?.data || [];
    const payments = detail?.payments || [];
    const isPayable = profile.settlement_type === "payable";
    const settlementActionNoun = isPayable ? t("Payment") : t("Receipt");
    const settledLabel = isPayable ? t("Paid Amount") : t("Received Amount");
    const remainingLabel = isPayable ? t("Remaining to Pay") : t("Remaining to Receive");
    const addSettlementLabel = isPayable ? t("Pay Store Seller") : t("Receive from Store Seller");

    const handlePaymentSuccess = (message) => {
        setSuccessMsg(message);
        fetchDetails(search);
    };

    return (
        <PageLayout>
            <Row className="mb-4">
                <Col xl={12}>
                    <div
                        className="mc-card p-4 rounded-4 shadow-sm border-0 d-flex justify-content-between align-items-center flex-wrap gap-3"
                        style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color: "#fff" }}
                    >
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="material-icons text-primary fs-3">storefront</span>
                                <h3 className="mc-breadcrumb-title m-0 text-white fw-bold">
                                    {seller.name || t("Store Seller Profile")}
                                </h3>
                            </div>
                            <p className="text-white-50 small m-0 ms-4">
                                {seller.email || t("Store Seller Settlement Details")}
                            </p>
                            <ul className="mc-breadcrumb-list mt-2 ms-4">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link text-white-50">{t("Home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="/admin/store-sellers" className="mc-breadcrumb-link text-white-50">{t("Store Sellers")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item text-primary fw-semibold">{t("Details")}</li>
                            </ul>
                        </div>
                        <div className="d-flex gap-2">
                            {summary.who_adds_payment === "admin" ? (
                                <Button
                                    variant="success"
                                    className="d-flex align-items-center gap-2 px-4 py-2 rounded-3 shadow fw-bold text-white border-0"
                                    onClick={() => setShowPaymentModal(true)}
                                    disabled={loading || Number(summary.remaining_amount || 0) <= 0}
                                    style={{ backgroundColor: "#10b981" }}
                                >
                                    <span className="material-icons">add_card</span>
                                    {addSettlementLabel}
                                </Button>
                            ) : summary.who_adds_payment === "self" ? (
                                <div className="small text-white-50 text-end" style={{ maxWidth: "320px" }}>
                                    {t("Receivable: the Store Seller adds the payment they send from their dashboard. Approve or reject it in Payments below.")}
                                    {Number(summary.pending_amount || 0) > 0 && (
                                        <div className="fw-semibold text-white mt-1">{t("Waiting for approval")}: Rs {money(summary.pending_amount)}</div>
                                    )}
                                </div>
                            ) : null}
                            <Button variant="secondary" onClick={() => navigate(`/admin/store-sellers/${sellerId}/edit`)}>
                                <span className="material-icons align-middle me-1 fs-6">edit</span>{t("Edit")}
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {error && <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4 shadow-sm rounded-3">{error}</Alert>}
            {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg("")} className="mb-4 shadow-sm rounded-3">{successMsg}</Alert>}

            <Row className="g-3 mb-4">
                <StatCard icon="sell" label={t("Total Products Sold")} value={summary.total_products_sold || 0} tone="primary" />
                <StatCard icon="payments" label={t("Total Sales")} value={`Rs ${money(summary.total_sales)}`} tone="info" />
                <StatCard icon="account_balance" label={t("Store Seller Share")} value={`Rs ${money(summary.store_seller_share)}`} tone="dark" />
                <StatCard icon="task_alt" label={settledLabel} value={`Rs ${money(summary.amount_paid_or_received)}`} tone="success" />
                <StatCard icon="pending_actions" label={remainingLabel} value={`Rs ${money(summary.remaining_amount)}`} tone="danger" />
            </Row>

            <Row className="mb-4">
                <Col xl={12}>
                    <div className="mc-card p-4 rounded-4 shadow-sm border-0 bg-white">
                        <Row className="g-4">
                            <Col lg={6}>
                                <h5 className="fw-bold border-bottom pb-2">{t("Account Information")}</h5>
                                <DetailRow label={t("Name")} value={seller.name} />
                                <DetailRow label={t("Nickname")} value={seller.nickname} />
                                <DetailRow label={t("Email")} value={seller.email} />
                                <DetailRow label={t("Phone")} value={seller.phone} />
                                <DetailRow label={t("CNIC")} value={profile.cnic} />
                                <DetailRow label={t("Status")} value={<Badge bg={profile.account_status === "approved" ? "success" : "secondary"}>{profile.account_status || "N/A"}</Badge>} />
                            </Col>
                            <Col lg={6}>
                                <h5 className="fw-bold border-bottom pb-2">{t("Store and Settlement")}</h5>
                                <DetailRow label={t("Store Name")} value={profile.store?.name} />
                                <DetailRow label={t("Settlement Type")} value={profile.settlement_type} />
                                <DetailRow label={t("Payment Type")} value={profile.payment_type ? `${profile.payment_type} (${profile.payment_value || 0})` : "N/A"} />
                                <DetailRow label={t("Settlement Status")} value={summary.payment_status} />
                                <DetailRow label={t("Storage")} value={summary.storage_preference === "admin_store" ? t("Company store (company pays shipping)") : t("Own store (seller pays shipping)")} />
                                <DetailRow label={t("Company Commission (delivered)")} value={`Rs ${money(summary.company_commission)}`} />
                                <DetailRow label={t("On Hold (pending / shipped orders)")} value={`Rs ${money(summary.hold_amount)}`} />
                                <DetailRow label={t("Opening Balance")} value={`Rs ${money(summary.opening_balance)}`} />
                                <DetailRow label={t("Total (opening + earned)")} value={`Rs ${money(summary.total_amount)}`} />
                                <DetailRow label={t("Bank")} value={profile.bank_name} />
                                <DetailRow label={t("IBAN")} value={profile.iban} />
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col xl={12}>
                    <div className="mc-card p-4 rounded-4 shadow-sm border-0 bg-white">
                        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3 border-bottom pb-3">
                            <div className="d-flex gap-2">
                                <Button variant={activeTab === "products" ? "primary" : "secondary"} onClick={() => setActiveTab("products")} className="px-4 py-2 rounded-3 fw-bold border-0 shadow-sm">
                                    <span className="material-icons align-middle me-1 fs-6">grid_view</span>{t("Sold Products")} ({products.length})
                                </Button>
                                <Button variant={activeTab === "payments" ? "primary" : "secondary"} onClick={() => setActiveTab("payments")} className="px-4 py-2 rounded-3 fw-bold border-0 shadow-sm">
                                    <span className="material-icons align-middle me-1 fs-6">receipt_long</span>{settlementActionNoun} {t("History")} ({payments.length})
                                </Button>
                            </div>
                            {activeTab === "products" && (
                                <div style={{ maxWidth: "320px", width: "100%" }}>
                                    <LabelFieldComponent type="search" placeholder="Search title, code..." value={search} onChange={(e) => { setSearch(e.target.value); fetchDetails(e.target.value); }} />
                                </div>
                            )}
                        </div>

                        {loading ? <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-2 text-muted fw-semibold">{t("Loading store seller data...")}</p></div> : activeTab === "products" ? (
                            <div className="table-responsive">
                                <Table hover className="mc-table align-middle m-0">
                                    <thead className="mc-table-head bg-light"><tr><th style={{ minWidth: "280px" }}>Product</th><th>Brand</th><th>Store</th><th>Category</th><th>Sale Amount</th></tr></thead>
                                    <tbody className="mc-table-body">
                                        {products.length ? products.map((product) => {
                                            const imagePath = product.card_img || product.img?.card_img || product.img?.first_img || product.first_img;
                                            const imageUrl = resolveImageUrl(imagePath);
                                            return <tr key={product.id}>
                                                <td><div className="d-flex align-items-center gap-3">{imageUrl ? <div className="rounded-3 border overflow-hidden shadow-sm" style={{ width: "48px", height: "48px", cursor: "pointer" }} onClick={() => setPreviewImage(imageUrl)}><img src={imageUrl} alt={product.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div> : <span className="material-icons text-muted">image_not_supported</span>}<div><span className="fw-semibold text-dark">{product.title || "N/A"}</span><div><Badge bg="light" className="text-dark border">Code: {product.product_code || "N/A"}</Badge></div></div></div></td>
                                                <td>{product.brand?.name || "N/A"}</td><td>{product.store?.name || profile.store?.name || "N/A"}</td><td>{product.category?.name || "N/A"}</td><td>${money(product.sell_price_pkr || product.sell_price_dollar || product.order_price)}</td>
                                            </tr>;
                                        }) : <tr><td colSpan={5} className="text-center py-5 text-muted"><span className="material-icons fs-1 d-block mb-2 text-black-50">search_off</span>{t("No products found for this store seller.")}</td></tr>}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="table-responsive"><Table hover className="mc-table align-middle m-0"><thead className="mc-table-head bg-light"><tr><th>#</th><th>Payment Date</th><th>Amount</th><th>Type</th><th>Status</th><th>Bank</th><th>Created By</th><th>Notes</th><th className="text-end">Action</th></tr></thead><tbody className="mc-table-body">{payments.length ? payments.map((payment, index) => <tr key={payment.id}><td>{index + 1}</td><td>{payment.payment_date || "N/A"}</td><td><Badge bg="success" className="fs-6 px-3 py-1">Rs {money(payment.amount)}</Badge></td><td className="text-capitalize">{payment.payment_type || "N/A"}</td><td><Badge bg={payment.status === "approved" ? "success" : payment.status === "cancelled" ? "secondary" : "warning"} text={payment.status === "pending" ? "dark" : undefined} className="text-capitalize">{payment.status || "pending"}</Badge>{payment.status !== "approved" && <div className="small text-muted">{t("Not counted until approved")}</div>}</td><td>{payment.bank?.name || payment.bank_name || "N/A"}</td><td><Badge bg="info" className="text-dark">{payment.created_by?.name || "System"}</Badge>{payment.added_by_self && <div className="small text-muted">{t("Added by the store seller")}</div>}</td><td className="text-muted">{payment.notes || "-"}</td><td className="text-end"><PaymentApprovalButton payment={payment} onApproved={() => fetchDetails()} t={t} /></td></tr>) : <tr><td colSpan={9} className="text-center py-5 text-muted">{t("No payments recorded yet.")}</td></tr>}</tbody></Table></div>
                        )}
                    </div>
                </Col>
            </Row>

            <Modal show={!!previewImage} onHide={() => setPreviewImage(null)} centered>
                <Modal.Header closeButton><Modal.Title className="fs-6">{t("Product Preview")}</Modal.Title></Modal.Header>
                <Modal.Body className="text-center"><img src={previewImage} alt="Product Preview" className="img-fluid rounded-3" style={{ maxHeight: "70vh", objectFit: "contain" }} /></Modal.Body>
            </Modal>

            <AddStoreSellerPaymentModal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} storeSellerId={sellerId} sellerInfo={profile} summary={summary} t={t} onSuccess={handlePaymentSuccess} />
        </PageLayout>
    );
}

function StatCard({ icon, label, value, tone }) {
    const backgrounds = { primary: "#e0f2fe", info: "#e0e7ff", dark: "#f1f5f9", success: "#dcfce7", danger: "#ffe4e6" };
    return <Col sm={6} md={4} xl={2.4}><Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ background: tone === "danger" ? "#fff1f2" : "#f8fafc" }}><Card.Body className="d-flex align-items-center p-3 gap-3"><div className={`rounded-3 p-3 text-${tone} d-flex align-items-center justify-content-center`} style={{ background: backgrounds[tone] }}><span className="material-icons">{icon}</span></div><div><span className={`${tone === "danger" ? "text-danger" : "text-muted"} text-uppercase fw-semibold small d-block mb-1`}>{label}</span><h4 className={`fw-bold m-0 ${tone === "success" ? "text-success" : tone === "danger" ? "text-danger" : "text-dark"}`}>{value}</h4></div></Card.Body></Card></Col>;
}

function DetailRow({ label, value }) {
    return <div className="d-flex justify-content-between gap-3 border-bottom py-2"><span className="text-muted">{label}</span><span className="text-end fw-semibold">{value ?? "N/A"}</span></div>;
}